import { getMemberDisplayName } from '@/helpers/user';
import { type MessageRecipient, RECIPIENT_TYPE } from '@/types';
import type { MessageRecipientsInfoResponse } from '@/types/responses/messageRecipientsInfoResponse.type';

export const encodeGroupRecipient = ({ groupId }: { groupId: string }): string => `group-${groupId}`;

export const encodeMemberRecipient = ({ memberId }: { memberId: string }): string => `member-${memberId}`;

export const encodeGroupMemberRecipient = ({ groupId, memberId }: { groupId: string; memberId: string }): string =>
  `member-${memberId}-group-${groupId}`;

export const replaceRecipientMemberId = ({
  value,
  nextMemberIdByCurrentId,
}: {
  value: string;
  nextMemberIdByCurrentId: Map<string, string>;
}): string => {
  if (!value.startsWith('member-')) {
    return value;
  }

  const memberPart = value.slice('member-'.length);
  const groupMatch = memberPart.match(/^(.+)-group-(.+)$/);
  if (groupMatch) {
    const [, memberId, groupId] = groupMatch;
    const nextMemberId = nextMemberIdByCurrentId.get(memberId);
    if (!nextMemberId || nextMemberId === memberId) {
      return value;
    }

    return encodeGroupMemberRecipient({ memberId: nextMemberId, groupId });
  }

  const nextMemberId = nextMemberIdByCurrentId.get(memberPart);
  if (!nextMemberId || nextMemberId === memberPart) {
    return value;
  }

  return encodeMemberRecipient({ memberId: nextMemberId });
};

export const encodeCommunityListRecipient = ({ listId }: { listId: string }): string => `community-list-${listId}`;

export const encodeCommunityListCardRecipient = ({ listId, cardId }: { listId: string; cardId: string }): string =>
  `community-card-${cardId}-list-${listId}`;

export const mapRecipientValueToMessageRecipient = ({ value }: { value: string }): MessageRecipient | null => {
  if (value.startsWith('community-card-')) {
    const match = value.match(/^community-card-(.+)-list-(.+)$/);
    if (!match) {
      return null;
    }

    const [, cardId, listId] = match;
    return {
      type: RECIPIENT_TYPE.COMMUNITY_LIST_CARD,
      listId,
      cardId,
    };
  }

  if (value.startsWith('community-list-')) {
    return {
      type: RECIPIENT_TYPE.COMMUNITY_LIST,
      listId: value.replace('community-list-', ''),
    };
  }

  if (value.startsWith('group-')) {
    return {
      type: RECIPIENT_TYPE.GROUP,
      groupId: value.replace('group-', ''),
    };
  }

  if (value.startsWith('member-')) {
    const memberPart = value.replace('member-', '');
    const groupMatch = memberPart.match(/^(.+)-group-(.+)$/);
    if (groupMatch) {
      const [, memberId, groupId] = groupMatch;
      return {
        type: RECIPIENT_TYPE.GROUP_MEMBER,
        memberId,
        groupId,
      };
    }

    return {
      type: RECIPIENT_TYPE.MEMBER,
      memberId: memberPart,
    };
  }

  return null;
};

export const mapRecipientsValueToMessageRecipients = ({ values }: { values: string[] }): MessageRecipient[] =>
  values
    .map((value) => mapRecipientValueToMessageRecipient({ value }))
    .filter((recipient): recipient is MessageRecipient => recipient !== null);

export const mapMessageRecipientToValue = ({ recipient }: { recipient: MessageRecipient }): string => {
  if (recipient.type === RECIPIENT_TYPE.GROUP) {
    return encodeGroupRecipient({ groupId: recipient.groupId });
  }

  if (recipient.type === RECIPIENT_TYPE.GROUP_MEMBER) {
    return encodeGroupMemberRecipient({ groupId: recipient.groupId, memberId: recipient.memberId });
  }

  if (recipient.type === RECIPIENT_TYPE.COMMUNITY_LIST) {
    return encodeCommunityListRecipient({ listId: recipient.listId });
  }

  if (recipient.type === RECIPIENT_TYPE.COMMUNITY_LIST_CARD) {
    return encodeCommunityListCardRecipient({ listId: recipient.listId, cardId: recipient.cardId });
  }

  return encodeMemberRecipient({ memberId: recipient.memberId });
};

export const buildRecipientLabelsFromRecipientsInfo = ({
  recipientsInfo,
}: {
  recipientsInfo: MessageRecipientsInfoResponse | null | undefined;
}): Map<string, string> => {
  const labels = new Map<string, string>();

  if (!recipientsInfo) {
    return labels;
  }

  recipientsInfo.groups.forEach((group) => {
    if (group.id && group.name) {
      labels.set(encodeGroupRecipient({ groupId: group.id }), group.name);
    }
  });

  recipientsInfo.members.forEach((member) => {
    if (!member.id) {
      return;
    }

    const label =
      getMemberDisplayName({
        firstName: member.firstName,
        lastName: member.lastName,
      }) || member.id;

    labels.set(encodeMemberRecipient({ memberId: member.id }), label);
  });

  (recipientsInfo.communityLists || []).forEach((list) => {
    if (list.id && list.name) {
      labels.set(encodeCommunityListRecipient({ listId: list.id }), list.name);
    }
  });

  (recipientsInfo.communityListCards || []).forEach((card) => {
    if (!card.cardId || !card.listId || !card.name) {
      return;
    }

    labels.set(encodeCommunityListCardRecipient({ listId: card.listId, cardId: card.cardId }), card.name);
  });

  return labels;
};
